import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getUpcomingAuctions } from '../../../../models/auctions';
import i18n from '../../../../i18n';
import AuctionCardNew from '../Auction/AuctionCard';
import AuctionGroupCardNew from '../Auction/AuctionGroupCard';
import CategoryPills from '../../helpers/Categorypills';
import AuctionFilterBar from '../../helpers/AuctionFilterBar';
import { SaudiBranchCard, SellerCtaCard } from '../../helpers/Sliderctacards';
import { SkeletonAuctionCard } from '../../../helpers/skeletons/SkeletonAuctionCard';
import { Link } from 'react-router-dom';

let current_lng = localStorage.getItem('I18N_LANGUAGE');

export class UpComingAuctionsNew extends React.Component {
  constructor(props) {
    super(props);
    const params = new URLSearchParams(window.location.search);
    this.state = {
      auctions: [],
      loading: false,
      currentIndex: 0,
      visibleCount: 3,
      sortBy: params.get('sort') || 'order',
      timeFilter: params.get('timeFilter') || 'all',
      categoryFilter: params.get('category') || 'all',
      counts: { total: 0, active: 0, upcoming: 0, expired: 0 },
    };
    this.slideNext = this.slideNext.bind(this);
    this.slidePrev = this.slidePrev.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  componentDidMount() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    this.loadAuctions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    const w = window.innerWidth;
    let visibleCount = 3;
    if (w <= 600) visibleCount = 1;
    else if (w <= 992) visibleCount = 2;
    this.setState({ visibleCount, currentIndex: 0 });
  }

  /* ── URL query helpers ── */

  setQueryParam(key, value) {
    const url = new URL(window.location);
    if (value && value !== 'all' && value !== 'order') {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.replaceState({}, '', url);
  }

  /* ── API call ── */

  async loadAuctions() {
    this.setState({ loading: true });
    try {
      let q = 'limit=100';
      const { categoryFilter } = this.state;
      if (categoryFilter && categoryFilter !== 'all') q += '&category=' + categoryFilter;

      const response = await getUpcomingAuctions(q);
      if (response) {
        let auctions = response.data || [];
        auctions = auctions.filter((a) => String(a.category) !== '10');
        let meta = response.meta || {};
        const counts = {
          total: meta.total || auctions.length,
          active: this.props.counts?.active,
          upcoming: this.props.counts?.upcoming,
          expired: this.props.counts?.previous,
        };

        const catMap = {};
        auctions.forEach((auc) => {
          const cd = auc.categoryDetails;
          if (cd && cd.id && cd.id !== 10) {
            if (!catMap[cd.id]) catMap[cd.id] = { ...cd, count: 0 };
            catMap[cd.id].count += 1;
          }
        });
        const categories = Object.values(catMap);

        auctions = this.sortAuctions(auctions);
        this.setState({ auctions, counts, categories, loading: false, currentIndex: 0 });
      }
    } catch (e) {
      this.setState({ loading: false, auctions: [], categories: [] });
    }
  }

  sortAuctions(data) {
    if (!data.length) return data;
    const { sortBy } = this.state;
    switch (sortBy) {
      case 'newest':
        return data.sort(
          (a, b) => new Date(b.created_at || b.start_date) - new Date(a.created_at || a.start_date)
        );
      case 'oldest':
        return data.sort(
          (a, b) => new Date(a.created_at || a.start_date) - new Date(b.created_at || b.start_date)
        );
      case 'price_asc':
        return data.sort(
          (a, b) =>
            (a.current_amount || a.start_amount || 0) - (b.current_amount || b.start_amount || 0)
        );
      case 'price_desc':
        return data.sort(
          (a, b) =>
            (b.current_amount || b.start_amount || 0) - (a.current_amount || a.start_amount || 0)
        );
      default:
        return data.sort((a, b) => (a.order || 0) - (b.order || 0));
    }
  }

  /* ── Filter handlers ── */

  handleSortChange = (sortBy) => {
    this.setQueryParam('sort', sortBy);
    this.setState({ sortBy }, () => this.loadAuctions());
  };

  handleTimeFilterChange = (timeFilter) => {
    this.setQueryParam('timeFilter', timeFilter);
    this.setState({ timeFilter }, () => {
      if (this.props.onTimeFilterChange) {
        this.props.onTimeFilterChange(timeFilter);
      }
      this.loadAuctions();
    });
  };

  handleCategoryChange = (categoryFilter) => {
    this.setQueryParam('category', categoryFilter);
    this.setState({ categoryFilter }, () => this.loadAuctions());
  };

  /* ── Slider nav ── */

  slideNext() {
    const totalSlides = this.state.auctions.length + 2;
    const max = Math.max(0, totalSlides - this.state.visibleCount);
    this.setState((s) => ({ currentIndex: Math.min(s.currentIndex + 1, max) }));
  }

  slidePrev() {
    this.setState((s) => ({ currentIndex: Math.max(s.currentIndex - 1, 0) }));
  }

  render() {
    const { auctions, loading, currentIndex, visibleCount, counts } = this.state;
    const totalSlides = auctions.length + 2; // +2 CTA cards
    const maxIndex = Math.max(0, totalSlides - visibleCount);
    const slideWidth = 100 / visibleCount;
    const translateX = -(currentIndex * slideWidth);
    const dotCount = Math.max(0, totalSlides - visibleCount + 1);

    return (
      <>
        <section className="ad-section">
          <div className="container-fluid">
            {loading ? (
              <div className="as-slider-outer">
                <SkeletonAuctionCard />
              </div>
            ) : (
              <>
                <div>
                  <div style={{ textAlign: 'center' }}>
                    <h2 className="ad-related-title">
                      {i18n.t('upcoming_auctions')}
                      &nbsp;{' '}
                      <span className="re-auc-topline__bids">{this.props.counts?.upcoming}</span>
                    </h2>
                  </div>

                  {/* ── Filter Bar ── */}
                  <div className="as-slider-outer">
                    <AuctionFilterBar
                      counts={counts}
                      sortBy={this.state.sortBy}
                      timeFilter={this.state.timeFilter}
                      categoryFilter={this.state.categoryFilter}
                      onSortChange={this.handleSortChange}
                      onTimeFilterChange={this.handleTimeFilterChange}
                      categories={this.state.categories}
                      activeCategory={this.state.categoryFilter}
                      onCategoryChange={this.handleCategoryChange}
                      showCount={true}
                    />
                  </div>

                  {/* ── Slider ── */}
                  <div className="as-slider-outer">
                    <button
                      className="as-arrow as-arrow--left"
                      onClick={this.slidePrev}
                      disabled={currentIndex === 0}
                    >
                      <ChevronLeft size={18} />
                    </button>

                    <div className="as-slider-track-wrap">
                      <div
                        className="as-slider-track"
                        style={{ transform: `translateX(${translateX}%)` }}
                      >
                        {auctions.map((auction, i) =>
                          !auction.is_a_group ? (
                            <div className="as-slide" key={auction.id || i}>
                              <AuctionCardNew auction={auction} auctionId={auction.id} />
                            </div>
                          ) : (
                            auction?.categoryDetails?.id != 10 && (
                              <div className="as-slide" key={`group-${i}`}>
                                <AuctionGroupCardNew
                                  group={auction}
                                  total_items={auctions.length}
                                  can_bid={true}
                                  type={this.props.type}
                                />
                              </div>
                            )
                          )
                        )}
                        <div className="as-slide" key="cta-saudi">
                          <SaudiBranchCard />
                        </div>
                        <div className="as-slide" key="cta-seller">
                          <SellerCtaCard />
                        </div>
                      </div>
                    </div>

                    <button
                      className="as-arrow as-arrow--right"
                      onClick={this.slideNext}
                      disabled={currentIndex >= maxIndex}
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>

                  {/* ── Dots ── */}
                  {dotCount > 1 && (
                    <div className="as-dots">
                      {Array.from({ length: dotCount }).map((_, i) => (
                        <button
                          key={i}
                          className={`as-dot ${i === currentIndex ? 'as-dot--active' : ''}`}
                          onClick={() => this.setState({ currentIndex: i })}
                        />
                      ))}
                    </div>
                  )}

                  {/* ── View All link ── */}
                  {auctions.length > 4 && (
                    <div className="view-all-wrap">
                      <Link to={'/auctions?sec=main&type=upcoming'} className="view-all-btn">
                        <span>{i18n.t('view_all')}</span>
                        {current_lng === 'ar' ? (
                          <ChevronLeft size={18} className="view-all-icon" />
                        ) : (
                          <ChevronRight size={18} className="view-all-icon" />
                        )}
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </>
    );
  }
}
